import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import EmsLogo from '@/components/EmsLogo'
import { getCollegeByAdminEmail } from '@/utils/userAccess'

interface TeacherRow {
  id?: number
  designation_en?: string
  designation_bn?: string
  teacher_name_en?: string
  teacher_name_bn?: string
  subject_en?: string
  department_en?: string
  department_bn?: string
  mobile?: string
  email?: string
  teacher_photo_url?: string
  priority?: number
  user_password?: string
  college_eiin?: number | string
  teacher_govt_id_no?: string
}

export default function TeacherManagementPage() {
  const navigate = useNavigate()
  const [teachers, setTeachers] = useState<TeacherRow[]>([])
  const [loading, setLoading] = useState(true)
  const [collegeEiin, setCollegeEiin] = useState<number | string | null>(null)
  const [collegeName, setCollegeName] = useState<string>('')
  const [statusMsg, setStatusMsg] = useState('')

  // Search
  const [searchQuery, setSearchQuery] = useState('')

  // Form / Modal State
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingRow, setEditingRow] = useState<TeacherRow | null>(null)
  const [formData, setFormData] = useState({
    teacher_name_en: '',
    teacher_name_bn: '',
    designation_en: 'Lecturer',
    designation_bn: '',
    department_en: '',
    department_bn: '',
    subject_en: '',
    mobile: '',
    email: '',
    teacher_govt_id_no: '',
    teacher_photo_url: '',
    priority: 1,
    user_password: ''
  })
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Load college and teachers
  const loadData = async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      const { data: authData } = await supabase.auth.getUser()
      const user = authData.user
      if (!user) {
        navigate('/login', { replace: true })
        return
      }

      const college = await getCollegeByAdminEmail(user.email)
      if (!college) {
        setStatusMsg('College profile not found for your admin email')
        setLoading(false)
        return
      }

      setCollegeEiin(college.college_eiin)
      setCollegeName(college.college_name)

      // Fetch teachers_database for this college
      const { data, error } = await supabase
        .from('teachers_database')
        .select('*')
        .eq('college_eiin', college.college_eiin)
        .order('priority', { ascending: true })

      if (error) throw error
      setTeachers(data || [])
      setStatusMsg(`Total ${data?.length || 0} teachers registered`)
    } catch (err: any) {
      console.error('TeacherManagementPage load error:', err)
      setErrorMsg(err?.message || 'Failed to load teachers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleOpenAdd = () => {
    setEditingRow(null)
    setFormData({
      teacher_name_en: '',
      teacher_name_bn: '',
      designation_en: 'Lecturer',
      designation_bn: '',
      department_en: '',
      department_bn: '',
      subject_en: '',
      mobile: '',
      email: '',
      teacher_govt_id_no: '',
      teacher_photo_url: '',
      priority: teachers.length + 1,
      user_password: ''
    })
    setErrorMsg(null)
    setShowAddModal(true)
  }

  const handleOpenEdit = (row: TeacherRow) => {
    setEditingRow(row)
    setFormData({
      teacher_name_en: row.teacher_name_en || (row as any).name || '',
      teacher_name_bn: row.teacher_name_bn || '',
      designation_en: row.designation_en || (row as any).designation || 'Lecturer',
      designation_bn: row.designation_bn || '',
      department_en: row.department_en || '',
      department_bn: row.department_bn || '',
      subject_en: row.subject_en || (row as any).subject || '',
      mobile: row.mobile || '',
      email: row.email || '',
      teacher_govt_id_no: row.teacher_govt_id_no || '',
      teacher_photo_url: row.teacher_photo_url || (row as any).teacher_photo || '',
      priority: row.priority || 1,
      user_password: row.user_password || ''
    })
    setErrorMsg(null)
    setShowAddModal(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!collegeEiin) {
      setErrorMsg('College EIIN missing')
      return
    }

    if (!formData.teacher_name_en.trim()) {
      setErrorMsg('Please enter teacher name in English')
      return
    }

    setSaving(true)
    setErrorMsg(null)

    try {
      const payload = {
        teacher_name_en: formData.teacher_name_en.trim(),
        teacher_name_bn: formData.teacher_name_bn.trim() || null,
        designation_en: formData.designation_en.trim() || null,
        designation_bn: formData.designation_bn.trim() || null,
        department_en: formData.department_en.trim() || null,
        department_bn: formData.department_bn.trim() || null,
        subject_en: formData.subject_en.trim() || null,
        mobile: formData.mobile.trim() || null,
        email: formData.email.trim() || null,
        teacher_govt_id_no: formData.teacher_govt_id_no.trim() || null,
        teacher_photo_url: formData.teacher_photo_url.trim() || null,
        priority: Number(formData.priority) || 1,
        user_password: formData.user_password.trim() || null,
        college_eiin: collegeEiin
      }

      if (editingRow && editingRow.id) {
        // Update
        const { error } = await supabase
          .from('teachers_database')
          .update(payload)
          .eq('id', editingRow.id)
          .eq('college_eiin', collegeEiin)

        if (error) throw error
      } else {
        // Insert
        const { error } = await supabase.from('teachers_database').insert([payload])
        if (error) throw error
      }

      setShowAddModal(false)
      await loadData()
    } catch (err: any) {
      console.error('handleSave teacher error:', err)
      setErrorMsg(err?.message || 'Failed to save teacher')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id?: number) => {
    if (!id || !collegeEiin) return
    if (!confirm('Are you sure you want to delete this teacher record?')) return

    try {
      const { error } = await supabase
        .from('teachers_database')
        .delete()
        .eq('id', id)
        .eq('college_eiin', collegeEiin)

      if (error) throw error
      await loadData()
    } catch (err: any) {
      alert('Delete error: ' + (err?.message || 'Failed to delete teacher'))
    }
  }

  const filteredTeachers = teachers.filter(t => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    const nameEn = t.teacher_name_en || (t as any).name || ''
    const nameBn = t.teacher_name_bn || ''
    const subj = t.subject_en || (t as any).subject || ''
    const desigEn = t.designation_en || (t as any).designation || ''
    const desigBn = t.designation_bn || ''
    const deptEn = t.department_en || ''
    const deptBn = t.department_bn || ''
    const govtId = t.teacher_govt_id_no || ''
    const email = t.email || ''
    const mobile = t.mobile || ''

    return (
      nameEn.toLowerCase().includes(q) ||
      nameBn.toLowerCase().includes(q) ||
      subj.toLowerCase().includes(q) ||
      desigEn.toLowerCase().includes(q) ||
      desigBn.toLowerCase().includes(q) ||
      deptEn.toLowerCase().includes(q) ||
      deptBn.toLowerCase().includes(q) ||
      govtId.toLowerCase().includes(q) ||
      email.toLowerCase().includes(q) ||
      mobile.includes(q)
    )
  })

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <EmsLogo size={34} />
          <button onClick={() => navigate('/dashboard')} style={styles.btnBack}>
            ← Dashboard
          </button>
          <div>
            <h1 style={styles.title}>Teachers Database</h1>
            <p style={styles.subtitle}>{collegeName} (EIIN: {collegeEiin})</p>
          </div>
        </div>

        <button onClick={handleOpenAdd} style={styles.btnAdd}>
          + Add New Teacher
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        {errorMsg && <div style={styles.errorBox}>{errorMsg}</div>}

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>Teacher Faculty Directory</h2>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by name, dept, subject, email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>{statusMsg}</span>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px', textAlign: 'center' }}>Photo</th>
                  <th style={styles.th}>Name & Designation</th>
                  <th style={styles.th}>Department & Subject</th>
                  <th style={styles.th}>Govt ID / Mobile</th>
                  <th style={styles.th}>Email</th>
                  <th style={{ ...styles.th, width: '70px', textAlign: 'center' }}>Priority</th>
                  <th style={{ ...styles.th, width: '140px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      Loading teachers...
                    </td>
                  </tr>
                ) : filteredTeachers.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      No teacher records found. Click <strong>+ Add New Teacher</strong> above to add.
                    </td>
                  </tr>
                ) : (
                  filteredTeachers.map((t, idx) => {
                    const photo = t.teacher_photo_url || (t as any).teacher_photo
                    const nameEn = t.teacher_name_en || (t as any).name
                    const nameBn = t.teacher_name_bn
                    const desigEn = t.designation_en || (t as any).designation || 'Lecturer'
                    const desigBn = t.designation_bn
                    const deptEn = t.department_en
                    const deptBn = t.department_bn
                    const subj = t.subject_en || (t as any).subject

                    return (
                      <tr key={t.id || idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {photo ? (
                            <img src={photo} alt="Photo" style={styles.photoThumb} />
                          ) : (
                            <div style={styles.photoPlaceholder}>👨‍🏫</div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 800, color: '#0f172a' }}>{nameEn}</div>
                          {nameBn && <div style={{ fontSize: '12px', color: '#475569' }}>{nameBn}</div>}
                          <div style={{ fontSize: '12px', color: '#ff5500', fontWeight: 700 }}>
                            {desigEn} {desigBn ? `(${desigBn})` : ''}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700, color: '#1e293b' }}>{subj || '—'}</div>
                          {(deptEn || deptBn) && (
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                              Dept: {deptEn || ''} {deptBn ? `(${deptBn})` : ''}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700, color: '#334155' }}>{t.mobile || '—'}</div>
                          {t.teacher_govt_id_no && (
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              ID: {t.teacher_govt_id_no}
                            </div>
                          )}
                        </td>
                        <td style={{ ...styles.td, fontSize: '13px', color: '#475569' }}>{t.email || '—'}</td>
                        <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>{t.priority ?? idx + 1}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button style={styles.btnEdit} onClick={() => handleOpenEdit(t)}>
                            Edit
                          </button>
                          <button style={styles.btnDelete} onClick={() => handleDelete(t.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add / Edit Teacher Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.2rem', fontWeight: 800 }}>
              {editingRow ? 'Edit Teacher Information' : 'Add New Teacher'}
            </h3>

            <form onSubmit={handleSave}>
              {/* Names */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={styles.label}>Teacher Name (English) *</label>
                  <input
                    type="text"
                    placeholder="e.g. Dr. Mohammad Ali"
                    style={styles.input}
                    value={formData.teacher_name_en}
                    onChange={e => setFormData({ ...formData, teacher_name_en: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label style={styles.label}>Teacher Name (Bangla)</label>
                  <input
                    type="text"
                    placeholder="যেমন: ড. মোহাম্মদ আলী"
                    style={styles.input}
                    value={formData.teacher_name_bn}
                    onChange={e => setFormData({ ...formData, teacher_name_bn: e.target.value })}
                  />
                </div>
              </div>

              {/* Designation */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={styles.label}>Designation (English)</label>
                  <input
                    type="text"
                    placeholder="e.g. Assistant Professor, Lecturer"
                    style={styles.input}
                    value={formData.designation_en}
                    onChange={e => setFormData({ ...formData, designation_en: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Designation (Bangla)</label>
                  <input
                    type="text"
                    placeholder="যেমন: সহকারী অধ্যাপক, প্রভাষক"
                    style={styles.input}
                    value={formData.designation_bn}
                    onChange={e => setFormData({ ...formData, designation_bn: e.target.value })}
                  />
                </div>
              </div>

              {/* Department */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={styles.label}>Department (English)</label>
                  <input
                    type="text"
                    placeholder="e.g. Department of Physics"
                    style={styles.input}
                    value={formData.department_en}
                    onChange={e => setFormData({ ...formData, department_en: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Department (Bangla)</label>
                  <input
                    type="text"
                    placeholder="যেমন: পদার্থবিজ্ঞান বিভাগ"
                    style={styles.input}
                    value={formData.department_bn}
                    onChange={e => setFormData({ ...formData, department_bn: e.target.value })}
                  />
                </div>
              </div>

              {/* Subject & Govt ID */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={styles.label}>Subject (English)</label>
                  <input
                    type="text"
                    placeholder="e.g. Mathematics, English"
                    style={styles.input}
                    value={formData.subject_en}
                    onChange={e => setFormData({ ...formData, subject_en: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Teacher Govt ID No</label>
                  <input
                    type="text"
                    placeholder="e.g. Govt Index / ID No"
                    style={styles.input}
                    value={formData.teacher_govt_id_no}
                    onChange={e => setFormData({ ...formData, teacher_govt_id_no: e.target.value })}
                  />
                </div>
              </div>

              {/* Mobile & Email */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={styles.label}>Mobile Number</label>
                  <input
                    type="text"
                    placeholder="01711..."
                    style={styles.input}
                    value={formData.mobile}
                    onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Email Address</label>
                  <input
                    type="email"
                    placeholder="teacher@school.edu"
                    style={styles.input}
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              {/* Photo & Password & Priority */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={styles.label}>Photo URL</label>
                  <input
                    type="text"
                    placeholder="https://..."
                    style={styles.input}
                    value={formData.teacher_photo_url}
                    onChange={e => setFormData({ ...formData, teacher_photo_url: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Password</label>
                  <input
                    type="password"
                    placeholder="Set password"
                    style={styles.input}
                    value={formData.user_password}
                    onChange={e => setFormData({ ...formData, user_password: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Priority (Order)</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={formData.priority}
                    onChange={e => setFormData({ ...formData, priority: Number(e.target.value) })}
                  />
                </div>
              </div>

              {errorMsg && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '14px' }}>{errorMsg}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" style={styles.btnCancel} onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={styles.btnSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Teacher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
    background: '#f8fafc',
    minHeight: '100vh',
    color: '#0f172a'
  },
  header: {
    background: '#ffffff',
    padding: '16px 32px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 50
  },
  btnBack: {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    color: '#475569',
    padding: '6px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#0f172a'
  },
  subtitle: {
    margin: 0,
    fontSize: '11px',
    color: '#ff5500',
    fontWeight: 800
  },
  btnAdd: {
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '12px',
    fontWeight: 800,
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(255, 85, 0, 0.3)'
  },
  main: {
    maxWidth: '1100px',
    margin: '32px auto',
    padding: '0 20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)'
  },
  searchInput: {
    padding: '8px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '13px',
    width: '240px',
    outline: 'none'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    background: '#f1f5f9',
    color: '#475569',
    fontWeight: 800,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  td: {
    padding: '12px 14px',
    verticalAlign: 'middle'
  },
  photoThumb: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    objectFit: 'cover',
    border: '1px solid #e2e8f0'
  },
  photoPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    margin: '0 auto'
  },
  btnEdit: {
    padding: '5px 12px',
    borderRadius: '6px',
    border: '1px solid #0366d6',
    background: '#0366d6',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    marginRight: '6px'
  },
  btnDelete: {
    padding: '5px 12px',
    borderRadius: '6px',
    border: '1px solid #ef4444',
    background: '#ef4444',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  errorBox: {
    padding: '12px 16px',
    background: '#fee2e2',
    color: '#dc2626',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: 600
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100
  },
  modalPanel: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '28px',
    width: '650px',
    maxWidth: '92%',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#475569'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none'
  },
  btnCancel: {
    padding: '9px 18px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    color: '#475569',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer'
  },
  btnSave: {
    padding: '9px 22px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '13px',
    cursor: 'pointer'
  }
}
